import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  computed,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';

@Component({
  selector: 'app-modern-date-time-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule, LabelsProviderPipe],
  templateUrl: './modern-date-time-picker.component.html',
  styleUrls: ['./modern-date-time-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModernDateTimePickerComponent implements OnInit, OnDestroy {
  @ViewChild('calendar') calendar?: Calendar;
  @Input() public label = '';
  @Input() public startingValue?: Date;
  @Input() public minDate?: Date;
  @Input() public maxDate?: Date;
  @Output() public dateOnSelection = new EventEmitter<Date | undefined>();

  /**
   * This event is sent from outside the date picker.
   * It will be triggered when we have a new value for the date picker that wasn't triggered using the date picker itself.
   * This will be mostly used by the changes on the query params.
   */
  @Input() public newValueTriggered$?: Observable<Date | undefined>;

  // Signals for reactive state
  readonly selectedDate = signal<Date | undefined>(undefined);
  readonly today = signal<Date>(new Date());
  readonly endOfToday = computed(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end;
  });
  // Use computed instead of effect to avoid signal writing issues
  readonly isFilled = computed(() => !!this.selectedDate());

  // Regular property for ngModel binding (PrimeNG doesn't support signals directly)
  public dateValue: Date | undefined;

  private _newValueSubscription$?: Subscription;
  private _lastEmittedValue: Date | null = null;
  private _dateChangeSubject$ = new Subject<Date>();
  private _dateChangeSubscription$?: Subscription;

  ngOnInit(): void {
    if (this.startingValue) {
      this.selectedDate.set(this.startingValue);
      this.dateValue = this.startingValue;
    }

    // Debounce time changes by 300ms to avoid multiple API calls while user is adjusting
    this._dateChangeSubscription$ = this._dateChangeSubject$.pipe(debounceTime(300)).subscribe((date: Date) => {
      this.dateOnSelection.emit(date);
    });

    if (this.newValueTriggered$) {
      this._newValueSubscription$ = this.newValueTriggered$.subscribe((newDate: Date | undefined) => {
        if (newDate) {
          const currentValue = this.selectedDate();
          if (!currentValue || newDate.getTime() !== currentValue.getTime()) {
            this.selectedDate.set(newDate);
            this.dateValue = newDate;
          }
        } else {
          if (this.selectedDate()) {
            this.selectedDate.set(undefined);
            this.dateValue = undefined;
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this._newValueSubscription$?.unsubscribe();
    this._dateChangeSubscription$?.unsubscribe();
    this._dateChangeSubject$.complete();
  }

  public onDateSelect(date: Date | null): void {
    if (date) {
      // Only emit if the date is different from the last emitted value
      if (!this._lastEmittedValue || date.getTime() !== this._lastEmittedValue.getTime()) {
        this._lastEmittedValue = date;
        this.selectedDate.set(date);
        this.dateValue = date;
        this.dateOnSelection.emit(date);
      }
    }
  }

  public onDateChange(date: Date | null | undefined): void {
    if (date) {
      // Update internal state immediately for UI
      this.selectedDate.set(date);
      this.dateValue = date;

      // Only emit if the date is different from the last emitted value
      // Use debounced subject to avoid rapid API calls while user adjusts time
      if (!this._lastEmittedValue || date.getTime() !== this._lastEmittedValue.getTime()) {
        this._lastEmittedValue = date;
        this._dateChangeSubject$.next(date);
      }
    }
  }

  public clearDate(event: Event): void {
    event.stopPropagation();
    this.selectedDate.set(undefined);
    this.dateValue = undefined;
    this._lastEmittedValue = null;
    this.dateOnSelection.emit(undefined);
  }

  public get displayValue(): string {
    const date = this.selectedDate();
    if (!date) return '';

    // Format: "Dec 18, 2025 13:37"
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    return date.toLocaleString('en-US', options);
  }

  public onTriggerClick(event: Event): void {
    // Don't open if clicking the clear button
    if ((event.target as HTMLElement).classList.contains('picker-clear')) {
      return;
    }

    // Toggle the calendar overlay
    if (this.calendar) {
      if (this.calendar.overlayVisible) {
        this.calendar.hideOverlay();
      } else {
        // Using toggle or directly calling onInputClick
        setTimeout(() => {
          this.calendar?.toggle();
        }, 0);
      }
    }
  }
}
